import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { v4 as uuidv4 } from 'uuid';
// import axios from 'axios';
import { config } from '../constants/config'
import { apiClient } from '../apiTools/apiClient';
const { getEncryptedField } = require('../utils/encryption-utils');

export const registerUser = createAsyncThunk('user/register',
  async ({ title, email, phone, password, role = 'bookCreator' }, { rejectWithValue }) => {
    localStorage.setItem('authToken', null); // clear out old data
    const userId = uuidv4() // this is used as the root folder name for the user S3 folder/files tree
    try {
      const response = await fetch(config.REGISTER_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ userId: userId, title, email, phone, password, role }),
      });

      if (!response.ok) {
        const errorData = await response.text();
        return rejectWithValue(errorData);
      }

      const data = await response.json();

      localStorage.setItem('authToken', data.token);
      // console.log('localStorage token', localStorage.getItem('authToken'))
      // let user = { ...data, ...{ title, email, phone, password } }
      return { ...data, role };
    } catch (error) {
      return rejectWithValue({ error: `Unable to register user. Please try again later.${error.message}` });
    }
  }
);
export const loginUser = createAsyncThunk(
  'user/login',
  async ({ email, password }, { rejectWithValue, dispatch }) => {
    try {
      // PR27 (P3-S5 incident remediation): removed a debug `console.log`
      // that printed the now-deleted REACT_APP_AWS_*_KEY env-var values —
      // neither is declared in any client/.env* file and any AKIA-shaped
      // 16-char string in a bundled JS file is a deploy-blocking grep gate
      // (see deploy-client.yml "Bundle leak gate" + runbook Session 6).
      // NOTE: paraphrasing avoids self-tripping the gate's source-map scan.
      const response = await fetch(config.LOGIN_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password }),
      });

      if (!response.ok) {
        const errorData = await response.text();
        return rejectWithValue(errorData);
      }
      const data = await response.json();

      // Store the token in localStorage
      localStorage.setItem('authToken', data.token);

      // Return the data with primaryRole for redirection
      const primaryRole = data.primaryRole || 'voiceActor';
      return {
        ...data,
        primaryRole,
        // Keep role for backward compatibility
        role: data.role || primaryRole
      };
    } catch (error) {
      return rejectWithValue({ error: `Unable to log in. Please try again later. ${error.message}` });
    }
  }
);

export const createUserDefaults = createAsyncThunk(
  'user/create-user',
  async ({ userId, email }, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('authToken');
      if (token) {
        const response = await fetch(
          config.CREATE_USER_META,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({ userId, email }),
          }
        );

        if (!response.ok) {
          const errorData = await response.json();
          return rejectWithValue(errorData);
        }
        const data = await response.json();
        return data;
      } else {
        return rejectWithValue({ error: 'Token not found in local storage.' });
      }
    } catch (error) {
      return rejectWithValue({ error: `Unable to create defaults. Please try again later. ${error.message}` });
    }
  }
);

// export const createUserS3Folders = createAsyncThunk(
//   'user/create-user-s3-folders',
//   async ({ email }, { rejectWithValue }) => {
//     try {
//       const token = localStorage.getItem('authToken');
//       if (token) {
//         const response = await fetch(
//           config.CREATE_USER_META,
//           {
//             method: 'POST',
//             headers: {
//               'Content-Type': 'application/json',
//               'Authorization': `Bearer ${token}`
//             },
//             body: JSON.stringify({ email }),
//           }
//         );

//         if (!response.ok) {
//           const errorData = await response.json();
//           return rejectWithValue(errorData);
//         }
//         const data = await response.json();
//         return data;
//       } else {
//         return rejectWithValue({ error: 'Token not found in local storage.' });
//       }
//     } catch (error) {
//       return rejectWithValue({ error: `Unable to create defaults. Please try again later. ${error.message}` });
//     }
//   }
// );


export const createUserS3Folders = createAsyncThunk(
  'user/create-s3-user-folders',
  async ({ userId, email }, { rejectWithValue }) => {
    try {
      // Use the utility function to get the encrypted field and token
      const result = await getEncryptedField(config.ENCRYPT_FIELD, email);

      if (result.error) {
        return rejectWithValue({ error: result.error });
      }

      const response = await fetch(config.CREATE_USER_S3_FOLDERS,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${result.token}`
          },
          // body: JSON.stringify({ folderName: result.encryptedField }), // Use encryptedFolderName as the value
          body: JSON.stringify({ folderName: userId }), // Use uuid generated in registration as user's folder name

        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        return rejectWithValue(errorData);
      }
      const data = await response.json();
      return data;
    } catch (error) {
      return rejectWithValue({ error: `Unable to create defaults. Please try again later. ${error.message}` });
    }
  }
);

export const getUserData = createAsyncThunk(
  'user/getUserData',
  async ({ token }, { rejectWithValue, dispatch }) => {
    try {
      // ⭐ THIS IS THE APP'S BOOTSTRAP CALL, and it used raw `fetch` — which is how an expired
      // session stayed invisible. `App.jsx` fires this on every page load where a token STRING
      // exists, so it is the first request of the session and the one best placed to discover the
      // session is dead. Going through `fetch` it reached none of the client's machinery: no 401
      // recovery (the token was left in storage and nobody was sent to login), no sliding-session
      // refresh (`X-Refresh-Token` dropped on the floor), no timeout. The failure was caught by the
      // `catch` below, turned into a rejected thunk, logged, and swallowed — so the app rendered as
      // logged-in with no profile until the next click produced a 401 from somewhere else.
      //
      // Through apiClient the 401 is handled once, in the one place that knows what to do with it.
      // Relative, like every other apiClient call — the instance already carries `config.BASE_URL`,
      // which is the same value `config.LOGGED_IN_USER` was built from.
      const response = await apiClient.post('/logged-in-user', { token });
      const data = response.data;

      // Return the data with primaryRole for redirection, similar to loginUser
      const primaryRole = data.primaryRole || 'voiceActor';
      return {
        ...data,
        primaryRole,
        // Keep role for backward compatibility
        role: data.role || primaryRole
      };
    } catch (error) {
      // An axios error carries the server's own body on `response.data`, which is where the useful
      // sentence lives ("Please authenticate. jwt expired"). `error.message` alone would say only
      // "Request failed with status code 401" — the shape that made this failure unreadable before.
      // ⚠️ A 401 has ALREADY been acted on by apiClient's interceptor (token cleared, redirect to
      // login) by the time this runs; this only decides what the rejected thunk carries.
      const body = error.response && error.response.data;
      return rejectWithValue(
        body || { error: `Unable to log in. Please try again later. ${error.message}` }
      );
    }
  }
);

const userSlice = createSlice({
  name: 'user',
  initialState: {
    data: null,
    rootFolder: null,
    userFolders: null,
    userAssetData: null,
    error: null,
    loading: false,
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(registerUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(registerUser.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action.payload;
        state.error = null;
      })
      .addCase(registerUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload.error;
      })
      .addCase(loginUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action.payload;
        state.error = null;
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload.error;
      })
      .addCase(createUserDefaults.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createUserDefaults.fulfilled, (state, action) => {
        state.loading = false;
        state.userFolders = action.payload;
        state.error = null;
      })
      .addCase(createUserDefaults.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload.error;
      })
      .addCase(createUserS3Folders.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createUserS3Folders.fulfilled, (state, action) => {
        state.loading = false;
        state.rootFolder = action.payload;
        state.error = null;
      })
      .addCase(createUserS3Folders.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload.error;
      })
      .addCase(getUserData.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getUserData.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action.payload;
        state.error = null;
      })
      .addCase(getUserData.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload.error;
      });
  },
});

export const userDataSelector = (state) => state.data;
export const getUserId = (state) => state.user.profile.data.userId;
export const selectCurrentUser = (state) => state.user.profile.data;
export default userSlice.reducer;
